/**
 * Cycle-level value/range governance overrides.
 * Lets users set cycle-level overrides (who can define/approve ranges, propose/approve values).
 * Null/unset = inherit from global; empty array = explicit "no one".
 * Tracks can still override below this layer.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { AuthorizationGate } from '../AuthorizationGate';
import { Link } from 'react-router-dom';
import { Shield, ChevronDown, ChevronUp, Edit, Save, X, RotateCcw, ExternalLink } from 'lucide-react';
import { useAttributeGovernanceStore, type CollectionMethod } from '@/stores/prioritization/attributeGovernanceStore';
import { VALUES_FROM } from '@/lib/valuesFrom';
import { useAttributesStore } from '@/stores/prioritization/attributesStore';
import { Select } from '@/components/ds';

const GOVERNANCE_CAP_LABELS: Record<string, string> = {
  VALUE_RANGE_DEFINE: 'Define ranges',
  SCALES_DEFINE: 'Define scales',
  VALUE_RANGE_APPROVE: 'Approve ranges',
  VALUE_ENTER: 'Enter values',
  VALUE_ACCEPT: 'Accept values',
  VALUE_REJECT: 'Reject values',
  OVERRIDE_APPLY: 'Override values',
};

const RANGE_DEFINE_CAPS = ['VALUE_RANGE_DEFINE', 'SCALES_DEFINE'];
const RANGE_APPROVE_CAPS = ['VALUE_RANGE_APPROVE'];
const VALUE_PROPOSE_CAPS = ['VALUE_ENTER', 'OVERRIDE_APPLY'];
const VALUE_APPROVE_CAPS = ['VALUE_ACCEPT', 'VALUE_REJECT', 'OVERRIDE_APPLY'];

// The same five words the attribute's settings page and the track's column use (1062).
const COLLECTION_METHODS: { id: CollectionMethod; label: string }[] = VALUES_FROM.map((a) => ({ id: a.id, label: a.label }));

export interface CycleGovernanceOverrideSectionProps {
  cycleId: string;
}

function CapabilityChips({
  selected,
  onChange,
  options,
  label,
}: {
  selected: string[];
  onChange: (v: string[]) => void;
  options: string[];
  label: string;
}) {
  const toggle = (id: string) => {
    if (selected.includes(id)) onChange(selected.filter((c) => c !== id));
    else onChange([...selected, id]);
  };
  return (
    <div>
      <p className="text-xs font-medium text-gray-600 mb-1">{label}</p>
      <div className="flex flex-wrap gap-1">
        {options.map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => toggle(id)}
            className={`px-2 py-1 rounded text-xs font-medium ${
              selected.includes(id) ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            {GOVERNANCE_CAP_LABELS[id] ?? id}
          </button>
        ))}
      </div>
    </div>
  );
}

function effectiveSourceLabel(hasOverride: boolean): string {
  return hasOverride ? 'Overridden at cycle' : 'Inherited from global';
}

export function CycleGovernanceOverrideSection({ cycleId }: CycleGovernanceOverrideSectionProps) {
  const templates = useAttributeGovernanceStore((s) => s.templates);
  const fetchTemplates = useAttributeGovernanceStore((s) => s.fetchFromSupabase);
  const fetchCycleOverrides = useAttributeGovernanceStore((s) => s.fetchCycleGovernanceOverrides);
  const getTemplateByKey = useAttributeGovernanceStore((s) => s.getTemplateByKey);
  const getCycleOverride = useAttributeGovernanceStore((s) => s.getCycleOverride);
  const getEffectiveConfig = useAttributeGovernanceStore((s) => s.getEffectiveConfig);
  const upsertCycleOverride = useAttributeGovernanceStore((s) => s.upsertCycleOverride);
  const deleteCycleOverride = useAttributeGovernanceStore((s) => s.deleteCycleOverride);

  const storedAttributes = useAttributesStore((s) => s.attributes);
  const attributes = useMemo(() => storedAttributes ?? [], [storedAttributes]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);
  useEffect(() => {
    if (cycleId) fetchCycleOverrides(cycleId);
  }, [cycleId, fetchCycleOverrides]);

  const attributesWithTemplates = useMemo(() => {
    const globalTemplates = templates.filter((t) => t.scopeKind === 'global' && t.isActive);
    return globalTemplates.map((t) => {
      const attr = attributes.find((a) => a.key === t.attributeKey);
      return { template: t, attributeKey: t.attributeKey, label: attr?.label ?? t.attributeKey };
    });
  }, [templates, attributes]);

  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<{
    rangeDefinerRoles: string[];
    rangeApproverRoles: string[];
    valueProposerRoles: string[];
    valueApproverRoles: string[];
    collectionMethod?: CollectionMethod;
    requiresEvidence?: boolean;
  }>({
    rangeDefinerRoles: [],
    rangeApproverRoles: [],
    valueProposerRoles: [],
    valueApproverRoles: [],
  });

  const startOverride = useCallback(
    (attributeKey: string) => {
      const template = getTemplateByKey(attributeKey);
      const override = getCycleOverride(cycleId, attributeKey);
      const effective = getEffectiveConfig(attributeKey, cycleId);
      setForm({
        rangeDefinerRoles: override?.rangeDefinerRoles ?? effective?.rangeDefinerRoles ?? template?.rangeDefinerRoles ?? [],
        rangeApproverRoles: override?.rangeApproverRoles ?? effective?.rangeApproverRoles ?? template?.rangeApproverRoles ?? [],
        valueProposerRoles: override?.valueProposerRoles ?? effective?.valueProposerRoles ?? template?.valueProposerRoles ?? [],
        valueApproverRoles: override?.valueApproverRoles ?? effective?.valueApproverRoles ?? template?.valueApproverRoles ?? [],
        collectionMethod: override?.collectionMethod ?? effective?.collectionMethod ?? template?.collectionMethod,
        requiresEvidence: override?.requiresEvidence ?? effective?.requiresEvidence ?? template?.requiresEvidence ?? false,
      });
      setEditingKey(attributeKey);
      setExpandedKey(attributeKey);
    },
    [cycleId, getTemplateByKey, getCycleOverride, getEffectiveConfig]
  );

  const cancelEdit = useCallback(() => {
    setEditingKey(null);
  }, []);

  const saveOverride = useCallback(async () => {
    if (!editingKey) return;
    setSaving(true);
    try {
      await upsertCycleOverride(cycleId, editingKey, {
        rangeDefinerRoles: form.rangeDefinerRoles,
        rangeApproverRoles: form.rangeApproverRoles,
        valueProposerRoles: form.valueProposerRoles,
        valueApproverRoles: form.valueApproverRoles,
        collectionMethod: form.collectionMethod,
        requiresEvidence: form.requiresEvidence,
      });
      setEditingKey(null);
    } finally {
      setSaving(false);
    }
  }, [cycleId, editingKey, form, upsertCycleOverride]);

  const revertToGlobal = useCallback(
    async (attributeKey: string) => {
      setSaving(true);
      try {
        await deleteCycleOverride(cycleId, attributeKey);
        setEditingKey(null);
        setExpandedKey(null);
      } finally {
        setSaving(false);
      }
    },
    [cycleId, deleteCycleOverride]
  );

  if (attributesWithTemplates.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <p className="text-sm text-gray-600">
          No global governance templates defined.{' '}
          <Link to="/prioritization/settings/attribute-permissions" className="text-primary-600 hover:text-primary-800 font-medium">
            Set global defaults (Attribute permissions)
          </Link>
          {' '}first; then you can override them here for this cycle.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
          <Shield className="h-4 w-4 text-gray-500" />
          Value/range governance (cycle)
        </h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Override who can define/approve ranges and propose/approve values for this cycle. Unset = inherit from global. Tracks can override further.{' '}
          <Link to="/prioritization/settings/attribute-permissions" className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-800 font-medium">
            Global defaults
            <ExternalLink className="h-3 w-3" />
          </Link>
        </p>
      </div>
      <div className="space-y-2">
        {attributesWithTemplates.map(({ attributeKey, label }) => {
          const override = getCycleOverride(cycleId, attributeKey);
          const effective = getEffectiveConfig(attributeKey, cycleId);
          const hasOverride = !!override;
          const isExpanded = expandedKey === attributeKey;
          const isEditing = editingKey === attributeKey;

          return (
            <div key={attributeKey} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
              <button
                type="button"
                onClick={() => setExpandedKey(isExpanded ? null : attributeKey)}
                className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <span className="font-medium text-gray-900">{label}</span>
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                      hasOverride ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {effectiveSourceLabel(hasOverride)}
                  </span>
                </div>
                {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
              </button>
              {isExpanded && (
                <div className="px-4 pb-4 pt-0 border-t border-gray-100 space-y-4">
                  {!isEditing ? (
                    <>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Range definers</p>
                          <p className="text-gray-700">
                            {(effective?.rangeDefinerRoles ?? []).length > 0
                              ? (effective?.rangeDefinerRoles ?? []).map((c) => GOVERNANCE_CAP_LABELS[c] ?? c).join(', ')
                              : '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Range approvers</p>
                          <p className="text-gray-700">
                            {(effective?.rangeApproverRoles ?? []).length > 0
                              ? (effective?.rangeApproverRoles ?? []).map((c) => GOVERNANCE_CAP_LABELS[c] ?? c).join(', ')
                              : '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Value proposers</p>
                          <p className="text-gray-700">
                            {(effective?.valueProposerRoles ?? []).length > 0
                              ? (effective?.valueProposerRoles ?? []).map((c) => GOVERNANCE_CAP_LABELS[c] ?? c).join(', ')
                              : '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Value approvers</p>
                          <p className="text-gray-700">
                            {(effective?.valueApproverRoles ?? []).length > 0
                              ? (effective?.valueApproverRoles ?? []).map((c) => GOVERNANCE_CAP_LABELS[c] ?? c).join(', ')
                              : '—'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => startOverride(attributeKey)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded"
                        >
                          <Edit className="h-4 w-4" />
                          {hasOverride ? 'Edit override' : 'Override for this cycle'}
                        </button>
                        {hasOverride && (
                          <button
                            type="button"
                            onClick={() => revertToGlobal(attributeKey)}
                            disabled={saving}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded"
                          >
                            <RotateCcw className="h-4 w-4" />
                            Revert to global
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <CapabilityChips
                          label="Range definers"
                          selected={form.rangeDefinerRoles}
                          onChange={(v) => setForm((f) => ({ ...f, rangeDefinerRoles: v }))}
                          options={RANGE_DEFINE_CAPS}
                        />
                        <CapabilityChips
                          label="Range approvers"
                          selected={form.rangeApproverRoles}
                          onChange={(v) => setForm((f) => ({ ...f, rangeApproverRoles: v }))}
                          options={RANGE_APPROVE_CAPS}
                        />
                        <CapabilityChips
                          label="Value proposers"
                          selected={form.valueProposerRoles}
                          onChange={(v) => setForm((f) => ({ ...f, valueProposerRoles: v }))}
                          options={VALUE_PROPOSE_CAPS}
                        />
                        <CapabilityChips
                          label="Value approvers"
                          selected={form.valueApproverRoles}
                          onChange={(v) => setForm((f) => ({ ...f, valueApproverRoles: v }))}
                          options={VALUE_APPROVE_CAPS}
                        />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-1">Values come from</p>
                        <Select
                          value={form.collectionMethod ?? ''}
                          onChange={(e) => setForm((f) => ({ ...f, collectionMethod: (e.target.value || undefined) as CollectionMethod | undefined }))}
                          className="bg-white text-gray-900 rounded border border-gray-300 text-sm py-1.5 px-2"
                        >
                          <option value="">As the workspace says</option>
                          {COLLECTION_METHODS.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.label}
                            </option>
                          ))}
                        </Select>
                      </div>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={form.requiresEvidence ?? false}
                          onChange={(e) => setForm((f) => ({ ...f, requiresEvidence: e.target.checked }))}
                          className="rounded border-gray-300"
                        />
                        Require evidence when proposing values
                      </label>
                      <div className="flex items-center gap-2 pt-2">
                        <AuthorizationGate
                          action="process.edit"
                          resource={{ type: 'cycle' as const, id: cycleId }}
                        >
                          <button
                            type="button"
                            onClick={saveOverride}
                            disabled={saving}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded disabled:opacity-50"
                          >
                            {saving ? 'Saving…' : (
                              <>
                                <Save className="h-4 w-4" /> Save
                              </>
                            )}
                          </button>
                        </AuthorizationGate>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          disabled={saving}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded"
                        >
                          <X className="h-4 w-4" /> Cancel
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
